import { useState, useEffect } from 'react';
import { getApiBase, isLocalMode, saveLocalData, getStorage } from '@/utils/storage';

export interface SubTodo {
  id: string;
  text: string;
  completed: boolean;
}

export type ReminderTiming = '1d' | '12h' | '1h' | '30m' | '10m' | number | null;

export interface Todo {
  id: string;
  text: string;
  completed: boolean;
  completedAt?: number;
  dueDate?: number; // timestamp
  feishuNotify?: boolean; // Keep for backward compatibility or general "notify" flag
  reminderTiming?: ReminderTiming;
  reminderSent?: boolean;
  priority?: 'high' | 'medium' | 'low';
  subTodos?: SubTodo[];
}

// Helper to trigger background check
const triggerReminderCheck = (todo: Todo) => {
    // For overdue tasks, we still want to notify if the user edited it.
    // We should NOT block if todo.completed is false (active).
    // If todo.completed is true, we usually don't remind.
    
    if (!todo.dueDate || !todo.reminderTiming || todo.completed) {
        // Debug
        // console.log('triggerReminderCheck skipped:', todo.text, 'Due:', todo.dueDate, 'Timing:', todo.reminderTiming, 'Completed:', todo.completed);
        return;
    }
    
    // Check if we are in Extension environment
    if (typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.sendMessage) {
        console.log('triggerReminderCheck sending message for:', todo.text);
        chrome.runtime.sendMessage({ 
            type: 'CHECK_REMINDER', 
            todo 
        }).catch(e => {
            // Ignore error if background script is not ready or not in extension mode
             console.warn('Trigger reminder check failed', e);
        });
    } else {
        console.warn('triggerReminderCheck: chrome.runtime not available');
    }
};

export const useTodos = () => {
  const [todos, setTodos] = useState<Todo[]>([]);

  useEffect(() => {
    const fetchTodos = async () => {
        try {
            if (isLocalMode()) {
                const data = await getStorage();
                setTodos(data.todos || []);
                return;
            }
            const apiBase = getApiBase();
            const res = await fetch(`${apiBase}/data`);
            const data = await res.json();
            setTodos(data.todos || []);
        } catch (e) {
            console.error('Failed to fetch todos', e);
        }
    };
    fetchTodos();
  }, []);

  const saveTodos = async (newTodos: Todo[]) => {
      setTodos(newTodos);
      try {
          if (isLocalMode()) {
              await saveLocalData({ todos: newTodos });
              return;
          }
          const apiBase = getApiBase();
          await fetch(`${apiBase}/data/todos`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(newTodos)
          });
      } catch (e) {
          console.error('Failed to save todos', e);
      }
  };

  const addTodo = (text: string, dueDate?: string, feishuNotify: boolean = false, reminderTiming: ReminderTiming = null, initialSubTodos: string[] = [], priority?: 'high' | 'medium' | 'low') => {
    if (!text.trim()) return;

    const todo: Todo = {
      id: Date.now().toString(),
      text,
      completed: false,
      dueDate: dueDate ? new Date(dueDate).getTime() : undefined,
      feishuNotify,
      reminderTiming,
      priority,
      subTodos: initialSubTodos.map(st => ({
          id: Date.now().toString() + Math.random().toString(36).substr(2, 5),
          text: st,
          completed: false
      }))
    };

    saveTodos([...todos, todo]);
    triggerReminderCheck(todo);
  };

  const toggleTodo = (id: string) => {
    const updatedTodos = todos.map(t => {
      if (t.id === id) {
        const completed = !t.completed;
        return { 
          ...t, 
          completed, 
          completedAt: completed ? Date.now() : undefined 
        };
      }
      return t;
    });
    saveTodos(updatedTodos);
    
    // If it was completed, we don't need to check reminders.
    // But if it was UN-completed (active again), we might want to check.
    const todo = updatedTodos.find(t => t.id === id);
    if (todo && !todo.completed) {
        triggerReminderCheck(todo);
    }
  };

  const deleteTodo = (id: string) => {
    saveTodos(todos.filter(t => t.id !== id));
  };

  const reorderTodos = (newTodos: Todo[]) => {
    saveTodos(newTodos);
    // Reordering usually doesn't need reminder check for each item.
  };
  
  // Sub-todo operations
  const addSubTodo = (todoId: string, text: string) => {
      if (!text.trim()) return;
      const updatedTodos = todos.map(t => {
          if (t.id === todoId) {
              const newSubTodo: SubTodo = {
                  id: Date.now().toString(),
                  text,
                  completed: false
              };
              return { ...t, subTodos: [...(t.subTodos || []), newSubTodo] };
          }
          return t;
      });
      saveTodos(updatedTodos);
      const todo = updatedTodos.find(t => t.id === todoId);
      if (todo) {
          triggerReminderCheck(todo);
      }
  };

  const toggleSubTodo = (todoId: string, subTodoId: string) => {
      const updatedTodos = todos.map(t => {
          if (t.id === todoId) {
              return {
                  ...t,
                  subTodos: (t.subTodos || []).map(st => 
                      st.id === subTodoId ? { ...st, completed: !st.completed } : st
                  )
              };
          }
          return t;
      });
      saveTodos(updatedTodos);
      const todo = updatedTodos.find(t => t.id === todoId);
      if (todo) {
          triggerReminderCheck(todo);
      }
  };

  const deleteSubTodo = (todoId: string, subTodoId: string) => {
      const updatedTodos = todos.map(t => {
          if (t.id === todoId) {
              return {
                  ...t,
                  subTodos: (t.subTodos || []).filter(st => st.id !== subTodoId)
              };
          }
          return t;
      });
      saveTodos(updatedTodos);
      const todo = updatedTodos.find(t => t.id === todoId);
      if (todo) {
          triggerReminderCheck(todo);
      }
  };
  
  const updateTodoReminder = (id: string, timing: ReminderTiming) => {
      const updatedTodos = todos.map(t => t.id === id ? { ...t, reminderTiming: timing, reminderSent: false } : t);
      saveTodos(updatedTodos);
      const todo = updatedTodos.find(t => t.id === id);
      if (todo) {
          triggerReminderCheck(todo);
      }
  };

  const updateTodoDueDate = (id: string, dueDate: number | undefined) => {
      const updatedTodos = todos.map(t => t.id === id ? { ...t, dueDate, reminderSent: false } : t);
      saveTodos(updatedTodos);
      const todo = updatedTodos.find(t => t.id === id);
      if (todo) {
          triggerReminderCheck(todo);
      }
  };

  const updateTodoText = (id: string, text: string) => {
      const updatedTodos = todos.map(t => t.id === id ? { ...t, text } : t);
      saveTodos(updatedTodos);
      const todo = updatedTodos.find(t => t.id === id);
      if (todo) {
          // Explicitly trigger check
          triggerReminderCheck(todo);
      }
  };

  const updateSubTodoText = (todoId: string, subTodoId: string, text: string) => {
      const updatedTodos = todos.map(t => {
          if (t.id === todoId) {
              return {
                  ...t,
                  subTodos: (t.subTodos || []).map(st => 
                      st.id === subTodoId ? { ...st, text } : st
                  )
              };
          }
          return t;
      });
      saveTodos(updatedTodos);
      const todo = updatedTodos.find(t => t.id === todoId);
      if (todo) {
          triggerReminderCheck(todo);
      }
  };

  const updateTodoPriority = (id: string, priority: 'high' | 'medium' | 'low' | undefined) => {
      const updatedTodos = todos.map(t => t.id === id ? { ...t, priority } : t);
      saveTodos(updatedTodos);
      const todo = updatedTodos.find(t => t.id === id);
      if (todo) {
          triggerReminderCheck(todo);
      }
  };

  return {
    todos,
    addTodo,
    toggleTodo,
    deleteTodo,
    reorderTodos,
    addSubTodo,
    toggleSubTodo,
    deleteSubTodo,
    updateTodoReminder,
    updateTodoDueDate,
    updateTodoText,
    updateSubTodoText,
    updateTodoPriority
  };
};
